import React, { useState, useContext, useRef } from 'react';
import AppContext from '../../contexts/AppContext';
import { Trophy, X, Video, Image } from 'lucide-react';
import Avatar from '../common/Avatar';
import * as firestoreService from '../../services/firestoreService';
import { uploadPostMedia } from '../../services/storageService';
import SecurityUtils from '../../utils/security';
import { generateVideoThumbnail, compressVideo } from '../../utils/media';

const PostCreateModal = ({ onClose, currentView }) => {
  const { currentUser, users, posts, setPosts, videoFiles, setVideoFiles, imageFiles, setImageFiles, showAlert } = useContext(AppContext);
  const isMarketplace = currentView === 'marketplace' || currentView === 'my-sales';
  const [newPost, setNewPost] = useState({
    content: '',
    video: null,
    videoPreview: null,
    images: [],
    imagePreviews: [],
    type: isMarketplace ? 'marketplace' : currentView === 'coaching' ? 'coaching' : 'general',
    // Marketplace specific fields
    title: '',
    price: '',
    condition: '',
    location: '',
    // Round Recap fields
    roundRecap: { courseName: '', score: '', bestHole: '', bestHoleScore: '', weather: 'sunny', notes: '' }
  });

  // Determine which post types to show based on current view
  const getPostTypeOptions = () => {
    if (currentView === 'feed') {
      return [
        { value: 'general', label: 'General' },
        { value: 'lookingToPlay', label: 'Looking to Play' },
        { value: 'roundRecap', label: 'Round Recap' }
      ];
    }
    // No options for marketplace and coaching - type is fixed
    return [];
  };

  const postTypeOptions = getPostTypeOptions();
  const showTypeSelector = postTypeOptions.length > 0;

  // Handle multiple image upload (up to 8)
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const maxImages = 8;
    const filesToUpload = files.slice(0, maxImages - newPost.images.length);

    filesToUpload.forEach(file => {
      const validation = SecurityUtils.validateFile(file, { maxSize: 10 * 1024 * 1024 });
      if (!validation.valid) { showAlert(`Image validation failed: ${validation.error}`, 'Error'); return; }
      if (validation.type !== 'image') { showAlert('Please select valid image files only.', 'Error'); return; }

      const reader = new FileReader();
      reader.onloadend = () => {
        setNewPost(prev => ({
          ...prev,
          images: [...prev.images, validation.sanitizedName],
          imagePreviews: [...prev.imagePreviews, reader.result],
          imageFiles: [...(prev.imageFiles || []), file],
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setNewPost(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
      imagePreviews: prev.imagePreviews.filter((_, i) => i !== index),
      imageFiles: (prev.imageFiles || []).filter((_, i) => i !== index),
    }));
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validation = SecurityUtils.validateFile(file, { maxSize: 50 * 1024 * 1024 });
      if (!validation.valid) { showAlert(`Video validation failed: ${validation.error}`, 'Error'); return; }
      if (validation.type !== 'video') { showAlert('Please select valid video files only.', 'Error'); return; }

      const reader = new FileReader();
      reader.onloadend = () => {
        setNewPost(prev => ({ ...prev, video: validation.sanitizedName, videoPreview: reader.result, videoFile: file }));
      };
      reader.readAsDataURL(file);
    }
  };

  const [postLoading, setPostLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const contentRef = useRef(null);

  const checkMention = (value, cursorPos) => {
    const before = value.slice(0, cursorPos);
    const match = before.match(/@(\w+)$/);
    if (match) {
      const query = match[1].toLowerCase();
      const filtered = users.filter(u =>
        u.handle && u.role !== 'admin' && u.role !== 'superadmin' && u.handle.startsWith(query)
      ).slice(0, 8);
      setMentionSuggestions(filtered);
    } else {
      setMentionSuggestions([]);
    }
  };

  const applyMention = (handle) => {
    const el = contentRef.current;
    const cursor = el.selectionStart;
    const value = newPost.content;
    const before = value.slice(0, cursor);
    const after = value.slice(cursor);
    const newBefore = before.replace(/@\w+$/, `@${handle} `);
    setNewPost(prev => ({ ...prev, content: newBefore + after }));
    setMentionSuggestions([]);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(newBefore.length, newBefore.length);
    }, 0);
  };

  const handleCreatePost = async () => {
    if (!newPost.content.trim()) {
      showAlert('Please enter some content for your post.', 'Error');
      return;
    }

    if (isMarketplace) {
      if (!newPost.title.trim()) {
        showAlert('Please enter a title for your listing.', 'Error');
        return;
      }
      if (!newPost.price || !newPost.condition || !newPost.location) {
        showAlert('Please fill in all required fields: Price, Condition, and Location.', 'Error');
        return;
      }
      if (isNaN(newPost.price) || parseFloat(newPost.price) <= 0) {
        showAlert('Please enter a valid price.', 'Error');
        return;
      }
    }

    if (newPost.type === 'roundRecap') {
      if (!newPost.roundRecap.courseName.trim() || !newPost.roundRecap.score) {
        showAlert('Please fill in Course Name and Total Score.', 'Error');
        return;
      }
    }

    if (currentView === 'coaching' && (!currentUser.isCoach || currentUser.status !== 'active')) {
      showAlert('Only active Coach accounts can post in the Coaching section.', 'Error');
      return;
    }

    setPostLoading(true);
    try {
      const sanitizedContent = SecurityUtils.sanitizeText(newPost.content);
      const postData = {
        userId: currentUser.id,
        content: sanitizedContent,
        type: newPost.type,
      };

      // Upload media files to Firebase Storage
      const postId = `p${Date.now()}`;
      const mediaFiles = [];

      let videoFileToUpload = newPost.videoFile;
      let thumbnailBlob = null;

      if (videoFileToUpload) {
        // Compress video and generate thumbnail
        setUploadProgress(-1); // indicates "compressing..."
        const [compressed, thumb] = await Promise.all([
          compressVideo(videoFileToUpload),
          generateVideoThumbnail(videoFileToUpload),
        ]);
        videoFileToUpload = compressed;
        thumbnailBlob = thumb;
        mediaFiles.push(videoFileToUpload);
      }
      if (newPost.imageFiles && newPost.imageFiles.length > 0) {
        mediaFiles.push(...newPost.imageFiles);
      }

      // Also upload thumbnail if we have one
      if (thumbnailBlob) {
        const thumbFile = new File([thumbnailBlob], 'thumb.jpg', { type: 'image/jpeg' });
        mediaFiles.push(thumbFile);
      }

      if (mediaFiles.length > 0) {
        setUploadProgress(0);
        const uploaded = await uploadPostMedia(postId, mediaFiles, currentUser.id, (progress) => setUploadProgress(progress));
        const thumbUpload = uploaded.find(u => u.name === 'thumb.jpg' && u.type === 'image');
        const imageUrls = uploaded.filter(u => u.type === 'image' && u.name !== 'thumb.jpg').map(u => u.url);
        const videoUrl = uploaded.find(u => u.type === 'video');
        if (imageUrls.length > 0) {
          postData.images = imageUrls;
          postData.imagePreviews = imageUrls;
        }
        if (videoUrl) {
          postData.video = { filename: videoUrl.name, url: videoUrl.url, thumbnail: thumbUpload?.url || null };
        }
      }

      if (newPost.type === 'coaching' && currentUser.location) {
        postData.location = currentUser.location;
      }

      if (newPost.type === 'marketplace') {
        postData.marketplace = {
          title: SecurityUtils.sanitizeText(newPost.title.trim()),
          price: parseFloat(newPost.price),
          condition: SecurityUtils.sanitizeText(newPost.condition),
          location: SecurityUtils.sanitizeText(newPost.location)
        };
      }

      if (newPost.type === 'roundRecap') {
        postData.roundRecap = {
          courseName: SecurityUtils.sanitizeText(newPost.roundRecap.courseName.trim()),
          score: parseInt(newPost.roundRecap.score),
          bestHole: newPost.roundRecap.bestHole ? parseInt(newPost.roundRecap.bestHole) : null,
          bestHoleScore: newPost.roundRecap.bestHoleScore ? parseInt(newPost.roundRecap.bestHoleScore) : null,
          weather: newPost.roundRecap.weather,
          notes: SecurityUtils.sanitizeText(newPost.roundRecap.notes.trim())
        };
      }

      const createdPost = await firestoreService.createPost(postData);

      // Notify @mentioned users (query Firestore directly so lookup is never stale)
      const mentionRegex = /@(\w+)/g;
      const mentionedHandles = new Set();
      let match;
      while ((match = mentionRegex.exec(sanitizedContent)) !== null) {
        mentionedHandles.add(match[1].toLowerCase());
      }
      mentionedHandles.forEach(handle => {
        firestoreService.getUserByHandle(handle).then(mentionedUser => {
          if (
            mentionedUser &&
            mentionedUser.id !== currentUser.id &&
            mentionedUser.role !== 'admin' &&
            mentionedUser.role !== 'superadmin'
          ) {
            return firestoreService.createNotification({
              targetUserId: mentionedUser.id,
              fromUserId: currentUser.id,
              type: 'mention',
              message: 'mentioned you in a post',
              postId: createdPost.id,
            });
          }
        }).catch(err => console.error('Error creating mention notification:', err));
      });

      onClose();
    } catch (err) {
      console.error('Error creating post:', err);
      showAlert('Failed to create post. Please try again.', 'Error');
    } finally {
      setPostLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-black border border-gray-700 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-black border-b border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">{isMarketplace ? 'Create Market Post' : 'Create Post'}</h2>
          <button onClick={onClose} className="text-white hover:text-gray-400">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Marketplace Title field */}
          {isMarketplace && (
            <div className="mb-3">
              <label className="block text-sm font-medium text-white mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newPost.title}
                onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                placeholder="e.g., Titleist TSR3 Driver"
                maxLength={30}
                className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
                autoFocus
              />
              <p className="text-xs text-gray-500 text-right mt-1">{newPost.title.length}/30</p>
            </div>
          )}

          <div className="relative">
            <textarea
              ref={contentRef}
              value={newPost.content}
              onChange={(e) => {
                const val = isMarketplace ? e.target.value.slice(0, 100) : e.target.value;
                setNewPost({ ...newPost, content: val });
                checkMention(val, e.target.selectionStart);
              }}
              onBlur={() => setTimeout(() => setMentionSuggestions([]), 150)}
              placeholder={
                isMarketplace
                  ? "Describe the item you're selling..."
                  : currentView === 'coaching'
                  ? "Share your coaching services and availability..."
                  : "Share your golf experience, tips, or swing video!"
              }
              className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] resize-none text-white bg-black"
              rows="4"
              maxLength={isMarketplace ? 100 : undefined}
              autoFocus={!isMarketplace}
            />
            {mentionSuggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 z-50 bg-gray-900 border border-gray-700 rounded-lg overflow-hidden shadow-xl mt-1">
                {mentionSuggestions.map(u => (
                  <button
                    key={u.id}
                    onMouseDown={(e) => { e.preventDefault(); applyMention(u.handle); }}
                    className="w-full text-left px-3 py-2 flex items-center space-x-3 hover:bg-gray-800"
                  >
                    <Avatar user={u} size="sm" />
                    <div>
                      <p className="text-sm font-bold text-white">{u.name}</p>
                      <p className="text-xs text-gray-400">@{u.handle}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {isMarketplace && (
            <p className="text-xs text-gray-500 text-right mt-1">{newPost.content.length}/100</p>
          )}

          {showTypeSelector && (
            <div className="mt-4">
              <select
                value={newPost.type}
                onChange={(e) => setNewPost({ ...newPost, type: e.target.value })}
                className="px-3 py-2 border border-gray-700 rounded-lg text-sm text-white bg-black w-full"
              >
                {postTypeOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Marketplace specific fields */}
          {isMarketplace && (
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Price <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white">$</span>
                  <input
                    type="number"
                    value={newPost.price}
                    onChange={(e) => setNewPost({ ...newPost, price: e.target.value })}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    className="w-full pl-8 pr-4 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Condition <span className="text-red-500">*</span>
                </label>
                <select
                  value={newPost.condition}
                  onChange={(e) => setNewPost({ ...newPost, condition: e.target.value })}
                  className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
                  required
                >
                  <option value="">Select condition...</option>
                  <option value="Worn">Worn</option>
                  <option value="Used">Used</option>
                  <option value="Like New">Like New</option>
                  <option value="Great">Great</option>
                  <option value="Excellent">Excellent</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Location <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newPost.location}
                  onChange={(e) => setNewPost({ ...newPost, location: e.target.value })}
                  placeholder="e.g., Brooklyn, NY"
                  className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
                  required
                />
              </div>
            </div>
          )}

          {/* Round Recap fields */}
          {newPost.type === 'roundRecap' && (
            <div className="mt-4 bg-gray-900 rounded-lg p-4 border border-gray-700 space-y-3">
              <h4 className="text-sm font-bold text-white flex items-center">
                <Trophy className="w-4 h-4 mr-2 text-[#16a34a]" />
                Round Details
              </h4>
              <div>
                <label className="block text-xs font-medium text-white mb-1">Course Name *</label>
                <input type="text" value={newPost.roundRecap.courseName}
                  onChange={(e) => setNewPost({ ...newPost, roundRecap: { ...newPost.roundRecap, courseName: e.target.value } })}
                  placeholder="e.g., Pebble Beach Golf Links"
                  className="w-full px-2 py-1 border border-gray-700 rounded-lg text-white bg-black text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-white mb-1">Total Score *</label>
                  <input type="number" value={newPost.roundRecap.score}
                    onChange={(e) => setNewPost({ ...newPost, roundRecap: { ...newPost.roundRecap, score: e.target.value } })}
                    placeholder="72" className="w-full px-2 py-1 border border-gray-700 rounded-lg text-white bg-black text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-white mb-1">Weather</label>
                  <select value={newPost.roundRecap.weather}
                    onChange={(e) => setNewPost({ ...newPost, roundRecap: { ...newPost.roundRecap, weather: e.target.value } })}
                    className="w-full px-2 py-1 border border-gray-700 rounded-lg text-white bg-black text-sm">
                    <option value="sunny">Sunny</option>
                    <option value="cloudy">Cloudy</option>
                    <option value="rainy">Rainy</option>
                    <option value="windy">Windy</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-white mb-1">Best Hole #</label>
                  <input type="number" min="1" max="18" value={newPost.roundRecap.bestHole}
                    onChange={(e) => setNewPost({ ...newPost, roundRecap: { ...newPost.roundRecap, bestHole: e.target.value } })}
                    placeholder="1-18" className="w-full px-2 py-1 border border-gray-700 rounded-lg text-white bg-black text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-white mb-1">Best Hole Score</label>
                  <input type="number" value={newPost.roundRecap.bestHoleScore}
                    onChange={(e) => setNewPost({ ...newPost, roundRecap: { ...newPost.roundRecap, bestHoleScore: e.target.value } })}
                    placeholder="e.g., 2" className="w-full px-2 py-1 border border-gray-700 rounded-lg text-white bg-black text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-white mb-1">Notes</label>
                <textarea value={newPost.roundRecap.notes}
                  onChange={(e) => setNewPost({ ...newPost, roundRecap: { ...newPost.roundRecap, notes: e.target.value } })}
                  placeholder="Any highlights or memorable moments..."
                  className="w-full px-2 py-1 border border-gray-700 rounded-lg text-white bg-black text-sm" rows="2" />
              </div>
            </div>
          )}

          {newPost.videoPreview && (
            <div className="mt-4 relative">
              <div className="aspect-video flex items-center justify-center bg-gray-800 rounded-lg">
                <div className="text-center p-6">
                  <Video className="w-12 h-12 text-white mx-auto mb-2" />
                  <p className="text-white text-sm font-medium mb-1">{newPost.video}</p>
                  <p className="text-white text-xs">Video ready to post</p>
                  <p className="text-white text-xs mt-1">(Preview unavailable in sandbox)</p>
                </div>
              </div>
              <button
                onClick={() => setNewPost({ ...newPost, video: null, videoPreview: null })}
                className="absolute top-2 right-2 bg-red-600 text-pure-white p-2 rounded-full hover:bg-red-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {newPost.imagePreviews.length > 0 && (
            <div className="mt-4 grid grid-cols-3 gap-1 rounded-lg overflow-hidden">
              {newPost.imagePreviews.map((preview, index) => (
                <div key={index} className="relative aspect-square">
                  <img src={preview} alt={`Preview ${index + 1}`} className="w-full h-full object-cover" />
                  <button
                    onClick={() => removeImage(index)}
                    className="absolute top-2 right-2 bg-black/60 text-pure-white p-1.5 rounded-full hover:bg-black/80"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 flex items-center space-x-3">
            <label className="cursor-pointer flex items-center space-x-2 px-2 py-1 border border-gray-700 rounded-lg text-sm hover:bg-gray-900 text-white">
              <input type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" disabled={!!newPost.video} />
              <Video className="w-4 h-4 text-white" />
              <span>Video</span>
            </label>

            <label className="cursor-pointer flex items-center space-x-2 px-2 py-1 border border-gray-700 rounded-lg text-sm hover:bg-gray-900 text-white">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                multiple
                disabled={newPost.images.length >= 8}
              />
              <Image className="w-4 h-4 text-white" />
              <span>Photo ({newPost.images.length}/8)</span>
            </label>
          </div>

          {postLoading && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-white">{uploadProgress === -1 ? 'Compressing video...' : 'Uploading...'}</span>
                {uploadProgress >= 0 && <span className="text-sm text-white">{uploadProgress}%</span>}
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${uploadProgress === -1 ? 'bg-yellow-500 animate-pulse' : 'bg-[#16a34a]'}`}
                  style={{ width: uploadProgress === -1 ? '100%' : `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={postLoading}
              className="px-3 py-1 text-white hover:bg-gray-800 rounded-lg disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCreatePost}
              disabled={postLoading}
              className="px-3 py-1 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d] disabled:opacity-50"
            >
              {postLoading ? 'Posting...' : 'Post'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


export default PostCreateModal;
